/*
 *  -----------------------------------------------------------------------
 *    <copyright file="observations-section.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { orderBy } from '@progress/kendo-data-query';
import moment from 'moment';

import PreviewExpandable from './preview-expandable';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useTranslation } from 'hooks';

import './observations-section.scss';
import { ReportFields } from 'queries/generated/fragments.generated';
import { ReportStatus } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import { getPanelName } from '@utils/cm-observations.service';

const ObservationsSection = ({
    data,
    dataSetId,
    dataSetDate,
    reports,
}: {
    data: any[];
    dataSetId: string;
    dataSetDate: any;
    reports: ReportFields[];
}) => {
    const { t } = useTranslation();

    const [items, setItems] = useState([]);

    useEffect(() => {
        if (data && dataSetId && dataSetDate) {
            const array = [...data]
                .filter((item: any) => {
                    const prevReport = reports.length
                        ? reports
                              .filter((element: any) => element.status === ReportStatus.FINAL)
                              .sort(
                                  (a: any, b: any) => new Date(b.created).getTime() - new Date(a.created).getTime(),
                              )[0]
                        : null;
                    const isGreaterThanPrevReport = prevReport
                        ? moment(prevReport.created) < moment(item.closedDatePopulated)
                        : true;
                    return (
                        (!item.siteId || item.siteId === Constants.ObjectId.Empty) &&
                        (item.dataSetId === dataSetId ||
                            (!item.closedDate && moment(item.dataSet?.dataCutDate) < moment(dataSetDate)) ||
                            (item.closedDate &&
                                moment(item.dataSet?.dataCutDate) < moment(dataSetDate) &&
                                isGreaterThanPrevReport))
                    );
                })
                .map((observation: any) => {
                    const _recentActionDate = observation.actions
                        .map((action: any) => action.requestDate)
                        .sort()
                        .reverse()[0];
                    const numberOfOpenActions = observation.actions.filter((action: any) => !action.completedDate);

                    const restructureActions = restructuredUpdatedCreatedBy(observation.actions);

                    const actions = restructureActions.map((action: any) => ({
                        ...action,
                        observationId: observation.id,
                        _ownerName: action.owner ? `${action.owner.firstName} ${action.owner.lastName}` : '',
                        _isCompletedDate: Boolean(action.completedDate),
                    }));

                    return {
                        ...observation,
                        _recentActionDate: _recentActionDate ? new Date(_recentActionDate) : null,
                        _openAction: numberOfOpenActions.length,
                        _numberOfActions: restructureActions.length,
                        _ownerName: observation.owner
                            ? `${observation.owner.firstName} ${observation.owner.lastName}`
                            : '',
                        _datasetDate: observation.dataSet?.dataCutDate ?? null,
                        _panelName: getPanelName(observation.panel, observation.type),
                        _isClosedDate: Boolean(observation.closedDate),
                        actions: orderBy(actions, [
                            { field: '_isCompletedDate', dir: 'asc' },
                            { field: 'updated', dir: 'desc' },
                        ]),
                    };
                });

            const newData = restructuredUpdatedCreatedBy(array);
            setItems(
                orderBy(newData, [
                    { field: '_isClosedDate', dir: 'asc' },
                    { field: 'updated', dir: 'desc' },
                ]),
            );
        }
    }, [data, dataSetDate, dataSetId, reports]);

    return (
        <PreviewExpandable header={t('cm:studyObservationsAndActions')} isExpanded={false}>
            {items?.length ? (
                <div className="observation-section-wrapper">
                    <p className="title-text">{t('ra:observationsAndActions')}</p>
                    {items.map((item: any) => (
                        <div className="observation-item-wrapper">
                            <div className="flex-wrap-text">
                                <p className="text-wrap">
                                    <p className="title">{t('id')}:</p> {item.recordId}
                                </p>
                                <p className="text-wrap">
                                    <p className="title">{t('ra:observationDate')}:</p>{' '}
                                    {formatDateUTC_DEPRECATED(item.observationDate, true, 'DD-MMM-YYYY')}
                                </p>
                                <p className="text-wrap">
                                    <p className="title">{t('cm:panelName')}:</p> {item._panelName}
                                </p>
                                <p className="text-wrap">
                                    <p className="title">{t('cm:datasetDate')}:</p>{' '}
                                    {formatDateUTC_DEPRECATED(item._datasetDate, true, 'DD-MMM-YYYY')}
                                </p>
                                <p className="text-wrap">
                                    <p className="title">{t('ra:openActions')}:</p> {item._openAction}
                                </p>
                                <p className="text-wrap">
                                    <p className="title">{t('ra:recentAction')}:</p>{' '}
                                    {formatDateUTC_DEPRECATED(item._recentActionDate, true, 'DD-MMM-YYYY')}
                                </p>
                                <p className="text-wrap">
                                    <p className="title">{t('owner')}:</p> {item._ownerName}
                                </p>
                                <p className="text-wrap">
                                    <p className="title">{t('ra:closedDate')}:</p>{' '}
                                    {formatDateUTC_DEPRECATED(item.closedDate, true, 'DD-MMM-YYYY')}
                                </p>
                                <p className="text-wrap">
                                    <p className="title">{t('ra:updatedBy')}:</p> {item._updatedByString}
                                </p>
                                <p className="text-wrap">
                                    <p className="title">{t('ra:updatedDate')}:</p>{' '}
                                    {formatDateUTC_DEPRECATED(item.updated, true, 'DD-MMM-YYYY')}
                                </p>
                            </div>
                            <p className="title-text">{t('ra:observation')}:</p>
                            <p className="content-text">{item.description}</p>
                            <p className="title-text">{t('ra:potentialCause')}:</p>
                            <p className="content-text">{item.potentialCause}</p>
                            <p className="title-text">{t('ra:confirmedCause')}:</p>
                            <p className="content-text">{item.confirmedCause}</p>
                            <p className="title-text">{t('ra:comments')}:</p>
                            <p className="content-text">{item.comments}</p>
                            <p className="title-text">{t('ra:action(s)')}</p>
                            {item.actions.length ? (
                                item.actions.map((element: any, index: number) => (
                                    <div className="observation-item-wrapper" key={element.recordId}>
                                        <div className="flex-wrap-text">
                                            <p className="text-wrap">
                                                <p className="title">{t('id')}:</p>
                                                {element.recordId}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:requestedDate')}:</p>
                                                {formatDateUTC_DEPRECATED(element.requestDate, true, 'DD-MMM-YYYY')}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('owner')}:</p>
                                                {element._ownerName}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:expectedCompletionDate')}:</p>
                                                {formatDateUTC_DEPRECATED(
                                                    element.expectedCompleteDate,
                                                    true,
                                                    'DD-MMM-YYYY',
                                                )}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:completedDate')}:</p>
                                                {formatDateUTC_DEPRECATED(element.completedDate, true, 'DD-MMM-YYYY')}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:updatedBy')}:</p>
                                                {item._updatedByString}
                                            </p>
                                            <p className="text-wrap">
                                                <p className="title">{t('ra:updatedDate')}:</p>
                                                {formatDateUTC_DEPRECATED(item.updated, true, 'DD-MMM-YYYY')}
                                            </p>
                                        </div>
                                        <p className="title-text">{t('description')}:</p>
                                        <p className="content-text">{element.description}</p>
                                        <p className="title-text">{t('cm:actionTaken')}:</p>
                                        <p className="content-text">{element.actionTaken}</p>
                                        <p className="title-text">{t('cm:rootCause')}:</p>
                                        <p className="content-text">{element.rootCause}</p>
                                        <p className="title-text">{t('result')}:</p>
                                        <p className="content-text">{element.result}</p>
                                        {index < item.actions.length - 1 ? <div className="divider-black" /> : null}
                                    </div>
                                ))
                            ) : (
                                <p className="no-records no-pt">{t('ra:noAssociatedActionRecords')}</p>
                            )}
                            <div className="divider-blue" />
                        </div>
                    ))}
                </div>
            ) : (
                <p className="no-records no-pt">{t('ra:noAssociatedObservationRecords')}</p>
            )}
        </PreviewExpandable>
    );
};

export default ObservationsSection;
